import { ICharacter } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { useMemo } from "react";

import BannedBanner from "@/components/BannedBanner";
import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

import HeroBanner from "../../HeroBanner";
import NsfwToggle from "../../NsfwToggle";
import { CharacterItemAction, DataSourceType, DisplayContext, HeaderComponentType, SearchListingConfig } from "../../types";
import WaitingQueueHeader from "../../WaitingQueuHeader";
import { useMyChatbotsSearch } from "../contexts/MyChatbotsSearchContext";
import CharacterHitsV2 from "../HitsV2";
import MyChatbotsFilterDropdown from "../MyChatbotsFilterDropdown";
import MyChatbotsSearchBar from "../MyChatbotsSearchBar";
import MyChatbotsSortDropdown from "../MyChatbotsSortDropdown";

interface MyChatbotsListingProps {
  // UI state
  nsfwToggle: boolean;
  setNsfwToggle: (value: boolean) => void;
  showNSFW: boolean;
  isPartner: boolean;

  // Config
  config?: SearchListingConfig;
  recommId: string;

  // Auth and permissions
  permissions?: string[];
  // Custom filters
  customFilters?: React.ReactNode;

  // Selection props (from MEMBER_SELECT context)
  onItemAction?: (action: CharacterItemAction, actionItem: ICharacter | undefined) => void;
  selectedItems?: string[];
  maxItemsToSelect?: number;
  onLimitReached?: (currentCount: number, maxCount: number) => void;
  dataSourceType?: DataSourceType;
}

const MyChatbotsListing = (props: MyChatbotsListingProps) => {
  const {
    nsfwToggle,
    setNsfwToggle,
    showNSFW,
    isPartner,
    config,
    recommId,
    permissions,
    customFilters,
    onItemAction,
    selectedItems,
    maxItemsToSelect,
    onLimitReached,
    dataSourceType = DataSourceType.MY_CHATBOTS,
  } = props;

  const {
    filteredAndSortedChatbots,
    localSearchQuery,
    localSortType,
    localFilterBy,
    localFilterValue,
    handleLocalSearch,
    handleLocalSort,
    handleLocalFilter,
  } = useMyChatbotsSearch();

  const isNsfwToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);

  const chatbotCountValues = useMemo(() => ({ count: filteredAndSortedChatbots?.length ?? 0 }), [filteredAndSortedChatbots?.length]);

  return (
    <>
      <Row className="flex-grow px-4 py-2.5 max-mob:p-2.5">
        <Col className={`gap-[18px]`}>
          {config?.headerComponent === HeaderComponentType.CUSTOM && config?.customHeader ? (
            config.customHeader
          ) : config?.headerComponent === HeaderComponentType.NONE ? null : (
            <>
              {!config?.hideBannedBanner && permissions?.includes("temporary_shadowban") ? <BannedBanner banType="shadow" /> : null}
              {config?.headerComponent !== HeaderComponentType.GROUP_CHAT && <HeroBanner />}
            </>
          )}

          <div className="flex gap-2.5 justify-between max-mob:flex-col">
            <Row className="gap-2.5 grow">
              <MyChatbotsSearchBar value={localSearchQuery} onChange={handleLocalSearch} />
            </Row>
            <Row className="gap-2.5">
              {!config?.hideNsfwToggle && isNsfwToggleAllowed ? (
                <NsfwToggle showNSFW={showNSFW} isNsfwToggled={nsfwToggle} setNsfwToggle={setNsfwToggle} />
              ) : null}
              <Row className="gap-2.5 max-mob:gap-[7px] max-mob:ml-auto">
                {customFilters}

                {config?.context !== DisplayContext.MEMBER_SELECT && (
                  <MyChatbotsFilterDropdown filterBy={localFilterBy} filterValue={localFilterValue} onChange={handleLocalFilter} />
                )}

                <MyChatbotsSortDropdown value={localSortType} onChange={handleLocalSort} />
              </Row>
            </Row>
          </div>

          {!config?.hideWaitingQueue && <WaitingQueueHeader />}

          {filteredAndSortedChatbots ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-colorStats">
              <Trans i18nKey="common:results.found" values={chatbotCountValues} />
            </Text>
          ) : null}

          <CharacterHitsV2
            context={config?.context || DisplayContext.MY_CHATBOTS}
            nsfwToggle={nsfwToggle}
            recommId={recommId}
            isPartner={isPartner}
            currentCategory=""
            onItemAction={onItemAction}
            selectedItems={selectedItems || []}
            maxItemsToSelect={maxItemsToSelect}
            onLimitReached={onLimitReached}
            dataSourceType={dataSourceType}
          />
        </Col>
      </Row>
    </>
  );
};

export default MyChatbotsListing;
